import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
  SPAN_STATUS_ERROR,
  SPAN_STATUS_OK,
  SPAN_STATUS_UNSET,
  type SpanStatus,
} from '@sentry/core';

// ── Helpers ──────────────────────────────────────────────────────────────

function addVideoBreadcrumb(
  message: string,
  level: Sentry.SeverityLevel,
  data?: Record<string, unknown>
) {
  Sentry.addBreadcrumb({ category: 'video-player', message, level, data });
}

/**
 * Strip query string and hash from a URL so signed tokens / auth params
 * are never sent to Sentry telemetry.
 */
function sanitizeSrc(src: string): string {
  try {
    const url = new URL(src);
    return `${url.origin}${url.pathname}`;
  } catch {
    // relative or non-standard URL — just strip everything after `?`
    return src.split('?')[0].split('#')[0];
  }
}

/** Grab connection info when available (Chrome / Edge / Android). */
function getConnectionInfo(): Record<string, unknown> | undefined {
  const conn = (navigator as Navigator & { connection?: NetworkInformation })
    .connection;
  if (!conn) return undefined;
  return {
    effectiveType: conn.effectiveType,
    downlinkMbps: conn.downlink,
    rtt: conn.rtt,
    saveData: conn.saveData,
  };
}

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

// ── Progress throttle (breadcrumbs can be very noisy) ────────────────────

const PROGRESS_THROTTLE_MS = 2000;

/** End an open span with a given status without leaving it dangling. */
function endSpan(span: Sentry.Span, status: SpanStatus) {
  span.setStatus(status);
  span.end();
}

// ── Hook ─────────────────────────────────────────────────────────────────

export function useVideoDownloadTracking(
  videoEl: HTMLVideoElement | null,
  src: string | undefined
) {
  const loadStartMs = useRef<number | null>(null); // null = loadstart not yet seen
  const stalledCount = useRef(0);
  const waitingCount = useRef(0);
  const lastProgressTs = useRef(0);
  const spanRef = useRef<Sentry.Span | undefined>(undefined);
  const didCaptureErrorRef = useRef(false); // dedup error captures per src

  useEffect(() => {
    if (!videoEl || !src) return;

    const safeSrc = sanitizeSrc(src);

    // Reset per-src state
    stalledCount.current = 0;
    waitingCount.current = 0;
    lastProgressTs.current = 0;
    didCaptureErrorRef.current = false;

    // If media is already mid-load when we attach (e.g. fast cache / preload),
    // seed the baseline so elapsed times are not measured from epoch.
    if (videoEl.readyState > 0 && loadStartMs.current === null) {
      loadStartMs.current = performance.now();
    }

    /** ms since load started; returns null if loadstart was never fired. */
    const elapsedSince = (): number | null =>
      loadStartMs.current !== null
        ? +(performance.now() - loadStartMs.current).toFixed(0)
        : null;

    // ── loadstart ────────────────────────────────────────────────────
    const onLoadStart = () => {
      // Close any orphaned span from a previous load attempt
      if (spanRef.current) {
        endSpan(spanRef.current, { code: SPAN_STATUS_ERROR, message: 'restarted' });
        spanRef.current = undefined;
      }

      loadStartMs.current = performance.now();
      stalledCount.current = 0;
      waitingCount.current = 0;
      lastProgressTs.current = 0;
      didCaptureErrorRef.current = false;

      const connection = getConnectionInfo();

      addVideoBreadcrumb('Video load started', 'info', {
        src: safeSrc,
        ...(connection && { connection }),
      });

      const spanAttributes: Record<string, string | number> = {
        'video.src': safeSrc,
      };
      if (connection?.effectiveType) {
        spanAttributes['video.connection_type'] = String(connection.effectiveType);
      }
      if (connection?.downlinkMbps !== undefined) {
        spanAttributes['video.downlink_mbps'] = Number(connection.downlinkMbps);
      }

      spanRef.current = Sentry.startInactiveSpan({
        name: 'video-player.download',
        op: 'resource.video',
        attributes: spanAttributes,
      });
    };

    // ── progress (throttled) ─────────────────────────────────────────
    const onProgress = () => {
      const now = performance.now();
      if (now - lastProgressTs.current < PROGRESS_THROTTLE_MS) return;
      lastProgressTs.current = now;

      const buffered = videoEl.buffered;
      if (buffered.length === 0) return;
      const bufferedEnd = buffered.end(buffered.length - 1);

      addVideoBreadcrumb('Video download progress', 'info', {
        src: safeSrc,
        bufferedSec: +bufferedEnd.toFixed(2),
        elapsedMs: elapsedSince(),
        duration: isFinite(videoEl.duration) ? +videoEl.duration.toFixed(2) : null,
      });
    };

    // ── loadeddata ───────────────────────────────────────────────────
    const onLoadedData = () => {
      addVideoBreadcrumb('Video first frame loaded', 'info', {
        src: safeSrc,
        timeToFirstFrameMs: elapsedSince(),
        videoWidth: videoEl.videoWidth,
        videoHeight: videoEl.videoHeight,
      });

      const elapsed = elapsedSince();
      if (elapsed !== null) {
        spanRef.current?.setAttributes({
          'video.time_to_first_frame_ms': elapsed,
        });
      }
    };

    // ── canplay ──────────────────────────────────────────────────────
    const onCanPlay = () => {
      const elapsed = elapsedSince();
      const buffered = videoEl.buffered;
      const bufferedEnd =
        buffered.length > 0 ? buffered.end(buffered.length - 1) : 0;

      addVideoBreadcrumb('Video can play', 'info', {
        src: safeSrc,
        timeToCanPlayMs: elapsed,
        bufferedSec: +bufferedEnd.toFixed(2),
        stalledCount: stalledCount.current,
        waitingCount: waitingCount.current,
      });

      if (spanRef.current) {
        spanRef.current.setAttributes({
          ...(elapsed !== null && { 'video.time_to_canplay_ms': elapsed }),
          'video.buffered_sec': +bufferedEnd.toFixed(2),
          'video.duration_sec': isFinite(videoEl.duration) ? videoEl.duration : -1,
          'video.stalled_count': stalledCount.current,
          'video.waiting_count': waitingCount.current,
          'video.video_width': videoEl.videoWidth,
          'video.video_height': videoEl.videoHeight,
        });
        endSpan(spanRef.current, { code: SPAN_STATUS_OK, message: 'ok' });
        spanRef.current = undefined;
      }
    };

    // ── canplaythrough ───────────────────────────────────────────────
    const onCanPlayThrough = () => {
      const buffered = videoEl.buffered;
      const bufferedEnd =
        buffered.length > 0 ? buffered.end(buffered.length - 1) : 0;

      addVideoBreadcrumb('Video can play through', 'info', {
        src: safeSrc,
        timeToCanPlayThroughMs: elapsedSince(),
        bufferedSec: +bufferedEnd.toFixed(2),
      });
    };

    // ── stalled ──────────────────────────────────────────────────────
    const onStalled = () => {
      stalledCount.current += 1;

      addVideoBreadcrumb('Video download stalled', 'warning', {
        src: safeSrc,
        stalledCount: stalledCount.current,
        elapsedMs: elapsedSince(),
        networkState: videoEl.networkState,
        readyState: videoEl.readyState,
        currentTime: videoEl.currentTime,
      });
    };

    // ── waiting ──────────────────────────────────────────────────────
    const onWaiting = () => {
      waitingCount.current += 1;

      addVideoBreadcrumb('Video waiting for data', 'warning', {
        src: safeSrc,
        waitingCount: waitingCount.current,
        elapsedMs: elapsedSince(),
        currentTime: videoEl.currentTime,
        readyState: videoEl.readyState,
      });
    };

    // ── error ────────────────────────────────────────────────────────
    const onError = () => {
      // Deduplicate: some browsers fire error multiple times for one failure
      if (didCaptureErrorRef.current) return;
      didCaptureErrorRef.current = true;

      const mediaErr = videoEl.error;
      const connection = getConnectionInfo();

      const errorData: Record<string, unknown> = {
        src: safeSrc,
        elapsedMs: elapsedSince(),
        networkState: videoEl.networkState,
        readyState: videoEl.readyState,
        errorCode: mediaErr?.code ?? null,
        errorMessage: mediaErr?.message ?? null,
        stalledCount: stalledCount.current,
        waitingCount: waitingCount.current,
        ...(connection && { connection }),
      };

      addVideoBreadcrumb('Video download error', 'error', errorData);

      if (spanRef.current) {
        spanRef.current.setAttributes({
          'video.error_code': mediaErr?.code ?? 0,
          'video.error_message': mediaErr?.message ?? 'unknown',
        });
        endSpan(spanRef.current, { code: SPAN_STATUS_ERROR, message: mediaErr?.message || 'video_load_error' });
        spanRef.current = undefined;
      }

      Sentry.withScope((scope) => {
        scope.setContext('videoPlayer', errorData);
        Sentry.captureException(
          new Error(
            `Video load failed: ${mediaErr?.message || 'unknown'} (code ${mediaErr?.code ?? 'n/a'})`
          )
        );
      });
    };

    // ── abort ────────────────────────────────────────────────────────
    const onAbort = () => {
      addVideoBreadcrumb('Video download aborted', 'warning', {
        src: safeSrc,
        elapsedMs: elapsedSince(),
      });

      if (spanRef.current) {
        // Abort is a user/system cancel — not an error
        endSpan(spanRef.current, { code: SPAN_STATUS_UNSET, message: 'cancelled' });
        spanRef.current = undefined;
      }
    };

    // ── suspend ──────────────────────────────────────────────────────
    const onSuspend = () => {
      const buffered = videoEl.buffered;
      const bufferedEnd =
        buffered.length > 0 ? buffered.end(buffered.length - 1) : 0;

      addVideoBreadcrumb('Video download suspended', 'info', {
        src: safeSrc,
        elapsedMs: elapsedSince(),
        bufferedSec: +bufferedEnd.toFixed(2),
      });
    };

    // ── emptied ──────────────────────────────────────────────────────
    const onEmptied = () => {
      addVideoBreadcrumb('Video emptied (media reset)', 'warning', { src: safeSrc });

      if (spanRef.current) {
        endSpan(spanRef.current, { code: SPAN_STATUS_UNSET, message: 'cancelled' });
        spanRef.current = undefined;
      }
    };

    const events = [
      ['loadstart', onLoadStart],
      ['progress', onProgress],
      ['loadeddata', onLoadedData],
      ['canplay', onCanPlay],
      ['canplaythrough', onCanPlayThrough],
      ['stalled', onStalled],
      ['waiting', onWaiting],
      ['error', onError],
      ['abort', onAbort],
      ['suspend', onSuspend],
      ['emptied', onEmptied],
    ] as const;

    for (const [event, handler] of events) {
      videoEl.addEventListener(event, handler);
    }

    return () => {
      for (const [event, handler] of events) {
        videoEl.removeEventListener(event, handler);
      }

      // Clean up any open span if the component unmounts mid-download
      if (spanRef.current) {
        endSpan(spanRef.current, { code: SPAN_STATUS_UNSET, message: 'cancelled' });
        spanRef.current = undefined;
      }

      // Reset baseline so a future effect run starts fresh
      loadStartMs.current = null;
    };
  }, [videoEl, src]);
}

